import type * as React from 'react';

import { cn } from '../lib/utils';

/**
 * Page-level heading (`<h1>`). Use one per page. `text-2xl font-bold`.
 */
export function Title({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'h1'>) {
  return (
    <h1 className={cn('text-2xl font-bold text-foreground', className)} {...props}>
      {children}
    </h1>
  );
}

/**
 * Major section heading within a page (`<h2>`). `text-lg font-semibold`.
 */
export function SectionTitle({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'h2'>) {
  return (
    <h2 className={cn('text-lg font-semibold text-foreground', className)} {...props}>
      {children}
    </h2>
  );
}

/**
 * Card / panel heading (`<h3>`). `text-base font-semibold`.
 */
export function CardTitle({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'h3'>) {
  return (
    <h3 className={cn('text-base font-semibold text-foreground', className)} {...props}>
      {children}
    </h3>
  );
}

/**
 * Descriptive paragraph under a Title or SectionTitle (`<p>`). `text-sm text-muted-foreground`.
 */
export function Subtitle({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'p'>) {
  return (
    <p className={cn('text-sm text-muted-foreground', className)} {...props}>
      {children}
    </p>
  );
}

type BodyTone = 'default' | 'muted' | 'error' | 'on-primary';
type BodyWeight = 'normal' | 'medium' | 'semibold';

const bodyToneClass: Record<BodyTone, string> = {
  default: 'text-foreground',
  muted: 'text-muted-foreground',
  error: 'text-error-600',
  'on-primary': 'text-on-primary',
};

const bodyWeightClass: Record<BodyWeight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
};

type BodyProps = React.ComponentPropsWithoutRef<'p'> & {
  /** Underlying element. Default `'p'`. Use `'span'` for inline body text, `'label'` for form labels. */
  as?: 'p' | 'span' | 'div' | 'label';
  weight?: BodyWeight;
  tone?: BodyTone;
  htmlFor?: string;
};

/**
 * Body text (`text-sm`, polymorphic). Use for primary content, list-item titles.
 * Prefer over inline `<p className="text-sm …">`.
 */
export function Body({
  as = 'p',
  weight = 'normal',
  tone = 'default',
  className,
  children,
  ...props
}: BodyProps) {
  const Tag = as as React.ElementType;
  return (
    <Tag
      className={cn('text-sm', bodyToneClass[tone], bodyWeightClass[weight], className)}
      {...props}
    >
      {children}
    </Tag>
  );
}

type CaptionTone = 'muted' | 'subtle';

const captionToneClass: Record<CaptionTone, string> = {
  muted: 'text-muted-foreground',
  subtle: 'text-neutral-400',
};

type CaptionProps = React.ComponentPropsWithoutRef<'p'> & {
  /** Underlying element. Default `'p'`. Use `'span'` for inline meta. */
  as?: 'p' | 'span' | 'div';
  tone?: CaptionTone;
};

/**
 * Small meta/caption text (`text-xs`, polymorphic).
 * Use for IDs, counts, footnotes, and other secondary information.
 */
export function Caption({
  as: Tag = 'p',
  tone = 'muted',
  className,
  children,
  ...props
}: CaptionProps) {
  return (
    <Tag className={cn('text-xs', captionToneClass[tone], className)} {...props}>
      {children}
    </Tag>
  );
}

export type { BodyProps, BodyTone, BodyWeight, CaptionProps, CaptionTone };
